/**
 * Composant CASH Balances - Soldes cash du propriétaire
 * Exploite l'endpoint /financial/owner/cash-balances
 * PARTICULARITÉ: 4 catégories (current/pending/pending_commission/completed_commission)
 * TERMINOLOGIE: Cash détenu, cash à venir, commissions à recevoir, commissions reçues
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Avatar,
  Collapse,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as BankTransferIcon,
  Schedule as PendingIcon,
  CheckCircle as CompletedIcon,
  SwapHoriz as ConvertIcon,
  Info as InfoIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Warning as OverdueIcon,
  MonetizationOn as CommissionIcon,
  Receipt as TaxIcon,
  Gavel as GavelIcon,
  TrendingDown as TrendingDownIcon,
  AddCircleOutline as AddCompensationIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatCurrency, formatDateTimeUniversal } from '../../utils/formatters';

// Import du composant modal (réutilisé)
import ReservationDetailsModal from './ReservationDetailsModal';

// Fonction pour formater la date avec l'heure (réutilisée)
const formatDateWithTime = (dateString, t) => {
  const unknownLabel = t ? t('unknown') : 'Unknown';
  if (!dateString) return unknownLabel;
  
  try {
    const date = new Date(dateString);
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return unknownLabel;
  }
};

// Fonction pour traduire les statuts de réservation (réutilisée)
const getReservationStatusLabel = (status, t) => {
  if (!status) return t ? t('unknown') : 'Unknown';

  const knownStatuses = new Set(['active', 'completed', 'cancelled', 'pending', 'confirmed']);
  if (t && knownStatuses.has(status)) {
    return t(`reservationStatuses.${status}`);
  }
  return status;
};

// Fonction pour obtenir la couleur et l'icône du statut de réservation (réutilisée)
const getReservationStatusProps = (status) => {
  const statusProps = {
    'active': { color: 'primary', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> },
    'completed': { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
    'cancelled': { color: 'error', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
    'pending': { color: 'warning', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
    'confirmed': { color: 'info', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> }
  };
  return statusProps[status] || { color: 'default', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> };
};

// Fonction pour obtenir les propriétés du statut de payin
const getPayinStatusProps = (status, t) => {
  const statusProps = {
    'pending':           { color: 'warning', icon: <ScheduleIcon sx={{ fontSize: 16 }} />,    labelKey: 'pending' },
    'processing':         { color: 'info',    icon: <ScheduleIcon sx={{ fontSize: 16 }} />,    labelKey: 'processing' },
    'completed':          { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, labelKey: 'completed' },
    'overdue':            { color: 'error',   icon: <OverdueIcon sx={{ fontSize: 16 }} />,     labelKey: 'overdue' },
    'cancelled':          { color: 'error',   icon: <CancelIcon sx={{ fontSize: 16 }} />,      labelKey: 'cancelled' },
    'link_sent':          { color: 'info',    icon: <ScheduleIcon sx={{ fontSize: 16 }} />,    labelKey: 'linkSent' },
    'payment_initiated':  { color: 'info',    icon: <ScheduleIcon sx={{ fontSize: 16 }} />,    labelKey: 'paymentInitiated' },
    'expired':            { color: 'error',   icon: <CancelIcon sx={{ fontSize: 16 }} />,      labelKey: 'expired' }
  };

  const props = statusProps[status] || { color: 'default', icon: <ScheduleIcon sx={{ fontSize: 16 }} />, labelKey: 'unknown' };
  return {
    ...props,
    label: t ? t(`cash.payinStatus.${props.labelKey}`) : status
  };
};

const OwnerCashBalancesWidget = () => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  const theme = useTheme();

  // États du composant
  const [cashData, setCashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  
  // États pour les détails et modaux
  const [expandedBalance, setExpandedBalance] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // État pour l'accordéon principal
  const [isMainExpanded, setIsMainExpanded] = useState(true);
  
  // États pour les détails des soldes
  const [balanceDetails, setBalanceDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  
  // État pour le modal d'information
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState({});

  /**
   * Charger les données CASH
   */
  const loadCashBalances = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      console.log('📊 [CashBalances] Fetching CASH balances data');

      const params = new URLSearchParams();
      if (targetCurrency) params.append('targetCurrency', targetCurrency);

      const response = await api.get(`/financial/owner/cash-balances?${params.toString()}`);

      if (!response || !response.data) {
        throw new Error(t('globalStats.error'));
      }

      // Extraire les données selon la structure backend
      let data;
      if (response.data.success && response.data.data) {
        data = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        data = response.data;
      } else {
        throw new Error(t('globalStats.error'));
      }

      console.log('✅ [CashBalances] CASH data loaded:', data);

      setCashData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ [CashBalances] Error loading CASH balances:', err);
      setError(err.message || t('globalStats.error'));
    } finally {
      setLoading(false);
    }
  }, [targetCurrency, t]);

  // Effet de chargement initial
  useEffect(() => {
    if (user) {
      loadCashBalances();
    }
  }, [user, loadCashBalances]);

  // Gestionnaire de rafraîchissement
  const handleRefresh = () => {
    loadCashBalances(false);
  };

  // Gestionnaire d'expansion des détails de transaction
  const handleToggleExpansion = useCallback((transactionId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [transactionId]: !prev[transactionId]
    }));
  }, []);

  // Gestionnaire d'ouverture du modal de détails de réservation
  const handleShowReservationDetails = useCallback((reservationId) => {
    setSelectedReservationId(reservationId);
    setIsModalOpen(true);
  }, []);

  // Gestionnaire de fermeture du modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedReservationId(null);
  }, []);

  // Gestionnaire de changement de devise cible
  const handleTargetCurrencyChange = (event) => {
    setTargetCurrency(event.target.value);
  };

  // Gestionnaire d'ouverture du modal d'information (4 catégories)
  const openInfoModal = (infoType, currency) => {
    const infoContent = {
      currentBalance: {
        title: t('cash.info.currentBalance.title'),
        content: t('cash.info.currentBalance.content', { currency })
      },
      pendingBalance: {
        title: t('cash.info.pendingBalance.title'),
        content: t('cash.info.pendingBalance.content', { currency })
      },
      pendingCommissionBalance: {
        title: t('cash.info.pendingCommissionBalance.title'),
        content: t('cash.info.pendingCommissionBalance.content', { currency })
      },
      completedCommissionBalance: {
        title: t('cash.info.completedCommissionBalance.title'),
        content: t('cash.info.completedCommissionBalance.content', { currency })
      }
    };

    setInfoModalContent(infoContent[infoType] || {});
    setIsInfoModalOpen(true);
  };

  // Gestionnaire de fermeture du modal d'information
  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
    setInfoModalContent({});
  };

  // Gestionnaire de clic sur une carte de solde
  const handleBalanceClick = async (currency, balanceType) => {
    const balanceKey = `${currency}_${balanceType}`;
    
    // Si déjà expansé, fermer
    if (expandedBalance === balanceKey) {
      setExpandedBalance(null);
      return;
    }
    
    // Ouvrir et charger les détails
    setExpandedBalance(balanceKey);
    
    // Charger les détails si pas déjà chargés
    if (!balanceDetails[balanceKey]) {
      await loadBalanceDetails(currency, balanceType, 1);
    }
  };

  // Charger les détails d'un solde
  const loadBalanceDetails = async (currency, balanceType, page = 1) => {
    const balanceKey = `${currency}_${balanceType}`;
    
    try {
      setDetailsLoading(prev => ({ ...prev, [balanceKey]: true }));

      console.log(`📊 [CashBalanceDetails] Loading details for ${currency} ${balanceType}, page ${page}`);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '8'
      });

      const fullUrl = `/financial/owner/cash-balance-details/${currency}/${balanceType}?${params.toString()}`;
      console.log(`🌐 [CashBalanceDetails] Full API URL:`, fullUrl);

      const response = await api.get(fullUrl);

      console.log(`📦 [CashBalanceDetails] Raw API response:`, {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      if (!response.data) {
        console.error(`❌ [CashBalanceDetails] No response.data received`);
        throw new Error(t('globalStats.error'));
      }

      let data;
      if (response.data.success && response.data.data) {
        console.log(`✅ [CashBalanceDetails] Using response.data.data structure`);
        data = response.data.data;
      } else if (response.data && response.data.items) {
        console.log(`✅ [CashBalanceDetails] Using direct response.data structure`);
        data = response.data;
      } else {
        console.error(`❌ [CashBalanceDetails] Unexpected data structure:`, response.data);
        throw new Error(t('globalStats.error'));
      }

      console.log(`✅ [CashBalanceDetails] Details loaded for ${currency} ${balanceType}:`, {
        itemsCount: data.items?.length || 0,
        pagination: data.pagination,
        commission_sum: data.commission_sum,
        taxes_sum: data.taxes_sum,
        total_sum: data.total_sum,
        firstItem: data.items?.[0] || 'No items'
      });

      // Mettre à jour les détails (pagination infinie)
      setBalanceDetails(prev => {
        const existingData = prev[balanceKey];
        console.log(`🔄 [CashBalanceDetails] Updating balance details:`, {
          balanceKey,
          page,
          hasExistingData: !!existingData,
          newItemsCount: data.items?.length || 0,
          hasMore: data.pagination?.hasMore
        });
        
        if (page === 1 || !existingData) {
          // Première page ou reset - remplacer les données
          return {
            ...prev,
            [balanceKey]: data
          };
        } else {
          // Pages suivantes - ajouter aux données existantes (pagination infinie)
          return {
            ...prev,
            [balanceKey]: {
              ...data,
              items: [...existingData.items, ...data.items],
              pagination: {
                ...data.pagination,
                page: data.pagination.page
              }
            }
          };
        }
      });

    } catch (err) {
      console.error('❌ [CashBalanceDetails] Error loading details:', err);
      setError(err.message || t('globalStats.error'));
    } finally {
      setDetailsLoading(prev => ({ ...prev, [balanceKey]: false }));
    }
  };

  // Charger plus de détails (pagination infinie)
  const loadMoreDetails = async (currency, balanceType) => {
    const balanceKey = `${currency}_${balanceType}`;
    const currentData = balanceDetails[balanceKey];
    
    if (!currentData?.pagination?.hasMore) {
      console.log(`🚫 [LoadMore] Cannot load more for ${balanceKey}:`, {
        hasData: !!currentData,
        hasMore: currentData?.pagination?.hasMore,
        pagination: currentData?.pagination
      });
      return;
    }
    
    const nextPage = currentData.pagination.page + 1;
    console.log(`📄 [LoadMore] Loading page ${nextPage} for ${balanceKey}`);
    
    // S'assurer qu'on ne charge pas déjà
    if (detailsLoading[balanceKey]) {
      console.log(`⏳ [LoadMore] Already loading for ${balanceKey}, skipping...`);
      return;
    }
    
    await loadBalanceDetails(currency, balanceType, nextPage);
  };

  // Composant de métrique pour chaque solde (adapté pour CASH)
  const BalanceMetric = ({ title, value, currency, icon, color = 'primary', details = null, infoType, balanceType }) => {
    const balanceKey = `${currency}_${balanceType}`;
    const isExpanded = expandedBalance === balanceKey;

    return (
      <Box 
        sx={{ 
          p: 2,
          border: 1,
          borderColor: isExpanded ? `${color}.main` : 'divider',
          borderRadius: 1,
          backgroundColor: alpha(theme.palette[color].main, isExpanded ? 0.1 : 0.05),
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.15),
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[2]
          }
        }}
        onClick={() => handleBalanceClick(currency, balanceType)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box color={theme.palette[color].main}>
            {icon}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={currency} 
              size="small" 
              color={color}
              variant="outlined"
            />
            {infoType && (
              <Tooltip title={t('cash.infoTooltip')}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    openInfoModal(infoType, currency);
                  }}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" sx={{ color: theme.palette[color].main }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Typography variant="h6" fontWeight="bold" color={`${color}.main`} mb={0.5}>
          {formatCurrency(value, currency)}
        </Typography>
        
        <Typography variant="subtitle2" color="text.primary" fontWeight="medium" mb={1}>
          {title}
        </Typography>
        
        {details && (
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        )}

        {isExpanded && (
          <Typography variant="caption" color={`${color}.main`} fontWeight="medium" sx={{ display: 'block', mt: 1 }}>
            {t('cash.balance.viewDetails')}
          </Typography>
        )}
      </Box>
    );
  };

  // Composant section par devise (adapté pour CASH - 4 catégories)
  const CurrencySection = ({ currency, balanceData }) => (
    <Card 
      elevation={2}
      sx={{ 
        mb: 2,
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        {/* En-tête de la section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {currency === 'DZD' ? '🇩🇿' : currency === 'EUR' ? '🇪🇺' : currency === 'USD' ? '🇺🇸' : '🌍'} {currency}
            </Typography>
          </Box>
          <Chip 
            label={t('cash.currencyTransactions', {
              count:
                (balanceData.payment_details?.current_count || 0) +
                (balanceData.payment_details?.pending_count || 0) +
                (balanceData.payin_details?.pending_count || 0) +
                (balanceData.payin_details?.completed_count || 0),
            })}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Métriques des soldes - 4 CATÉGORIES pour CASH */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <BalanceMetric
              title={t('cash.balance.current')}
              value={balanceData.current_balance}
              currency={currency}
              icon={<CheckCircleIcon />}
              color="success"
              details={t('cash.balance.currentCount', {
                count: balanceData.payment_details?.current_count || 0,
              })}
              infoType="currentBalance"
              balanceType="current"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BalanceMetric
              title={t('cash.balance.pending')}
              value={balanceData.pending_balance}
              currency={currency}
              icon={<PendingIcon />}
              color="info"
              details={t('cash.balance.pendingCount', {
                count: balanceData.payment_details?.pending_count || 0,
              })}
              infoType="pendingBalance"
              balanceType="pending"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BalanceMetric
              title={t('cash.balance.pendingCommission')}
              value={balanceData.pending_commission_balance}
              currency={currency}
              icon={<ScheduleIcon />}
              color="warning"
              details={t('cash.balance.pendingCommissionCount', {
                count: balanceData.payin_details?.pending_count || 0,
              })}
              infoType="pendingCommissionBalance"
              balanceType="pending_commission"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BalanceMetric
              title={t('cash.balance.completedCommission')}
              value={balanceData.completed_commission_balance}
              currency={currency}
              icon={<CompletedIcon />}
              color="success"
              details={t('cash.balance.completedCommissionCount', {
                count: balanceData.payin_details?.completed_count || 0,
              })}
              infoType="completedCommissionBalance"
              balanceType="completed_commission"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Rendu conditionnel pour les états de chargement et d'erreur
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" mt={2} color="text.secondary">
              {t('cash.loading')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                {t('common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!cashData) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('cash.noData')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* En-tête principal - Accordéon */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          onClick={() => setIsMainExpanded(!isMainExpanded)}
          sx={{
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <BankTransferIcon sx={{ color: 'white' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                {t('cash.title')}
              </Typography>
              {lastUpdated && isMainExpanded && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {t('cash.lastUpdated')} {formatDateTimeUniversal(lastUpdated)}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={(e) => { e.stopPropagation(); handleRefresh(); }} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
            <IconButton sx={{ color: 'white' }}>
              {isMainExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Contenu rétractable */}
        <Collapse in={isMainExpanded}>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('cash.description')}
          </Typography>

        {/* Sections par devise */}
        {Object.entries(cashData.cash_balances).map(([currency, balanceData]) => (
          <React.Fragment key={currency}>
            <CurrencySection 
              currency={currency} 
              balanceData={balanceData} 
            />
            
            {/* Section des détails expansés pour cette devise */}
            {expandedBalance && expandedBalance.startsWith(currency + '_') && (
              <Card 
                sx={{ 
                  mb: 3,
                  border: 2,
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.02)
                }}
              >
                <CardContent>
                  {(() => {
                    const [currencyFromBalance, balanceType] = expandedBalance.split('_');
                    const detailsData = balanceDetails[expandedBalance];
                    const isLoadingDetails = detailsLoading[expandedBalance];
                    
                    return (
                      <>
                        {/* En-tête des détails */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                              {(() => {
                                const typeKey =
                                  balanceType === 'current'
                                    ? 'typeCurrent'
                                    : balanceType === 'pending'
                                      ? 'typePending'
                                      : balanceType === 'pending_commission'
                                        ? 'typePendingCommission'
                                        : 'typeCompletedCommission';

                                return t('cash.details.header', {
                                  currency: currencyFromBalance,
                                  type: t(`cash.details.${typeKey}`),
                                });
                              })()}
                            </Typography>
                            {detailsData && (
                              <Chip 
                                label={(() => {
                                  const [currency, balanceType] = expandedBalance.split('_');
                                  
                                  // Maintenant c'est toujours des réservations avec payments intégrés
                                  const reservationCount = detailsData.items?.length || 0;
                                  const totalPayments = detailsData.items?.reduce((sum, reservation) => {
                                    return sum + (reservation.payments?.length || 0);
                                  }, 0) || 0;
                                  
                                  if (balanceType === 'pending_commission' || balanceType === 'completed_commission') {
                                    return t('cash.details.reservationsWithCommissions', { count: reservationCount });
                                  } else {
                                    return t('cash.details.reservationsWithPayments', {
                                      reservations: reservationCount,
                                      payments: totalPayments,
                                    });
                                  }
                                })()}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <IconButton 
                            onClick={() => setExpandedBalance(null)}
                            color="primary"
                          >
                            <ExpandLessIcon />
                          </IconButton>
                        </Box>

                        {/* Contenu des détails */}
                        <Box>
                          {isLoadingDetails ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                              <CircularProgress size={32} color="primary" />
                              <Typography variant="body1" ml={2} color="text.secondary">
                                {t('cash.details.loading')}
                              </Typography>
                            </Box>
                          ) : detailsData && detailsData.items.length > 0 ? (
                            <>
                              {/* Affichage des détails - NOUVEAU: Groupage par réservation pour pending_commission/completed_commission */}
                              {(() => {
                                console.log(`🎯 [CashBalanceDetails] Rendering details for ${expandedBalance}:`, {
                                  detailsData,
                                  itemsCount: detailsData.items.length,
                                  firstItem: detailsData.items[0],
                                  allItems: detailsData.items
                                });

                                const [currency, ...balanceTypeParts] = expandedBalance.split('_');
                                const balanceType = balanceTypeParts.join('_');
                                
                                // NOUVEAU: Pour tous les types, traiter les réservations avec payments intégrés
                                if (balanceType === 'current' || balanceType === 'pending') {
                                  // Pour current et pending: afficher les réservations avec leurs 4 payments
                                  const reservationsToRender = detailsData.items.map((reservation, index) => {
                                    const reservationKey = `${expandedBalance}_${reservation.reservation_id}`;
                                    const isExpanded = expandedDetails[reservationKey];
                                    
                                    console.log(`🎯 [Current/Pending] Rendering reservation:`, {
                                      reservation_id: reservation.reservation_id,
                                      payments_count: reservation.payments?.length || 0,
                                      payments: reservation.payments
                                    });
                                    
                                    return (
                                      <Card 
                                        key={reservationKey} 
                                        sx={{ 
                                          mb: 2, 
                                          border: '2px solid', 
                                          borderColor: 'primary.main',
                                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                                        }}
                                      >
                                        {/* En-tête de la réservation */}
                                        <CardContent 
                                          sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                                          }}
                                          onClick={() => handleToggleExpansion(reservationKey)}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                                              <HomeIcon />
                                            </Avatar>
                                            
                                            <Box sx={{ flex: 1 }}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                  <Typography variant="subtitle1" fontWeight="bold">
                                                    {reservation.listing_title || t('cash.details.unknownListing')}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {t('cash.details.dateRange', {
                                                      from: new Date(reservation.start_date).toLocaleDateString(),
                                                      to: new Date(reservation.end_date).toLocaleDateString(),
                                                    })}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    <PersonIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {reservation.renter_name}
                                                  </Typography>
                                                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                                                    {t('cash.details.paymentsCashCount', {
                                                      count: reservation.payments?.filter(p => !['dispute_refund', 'damage_compensation'].includes(p.payment_type)).length || 0,
                                                    })}
                                                    {reservation.disputes?.length > 0 && (
                                                      <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                                        · {t('cash.details.disputeCount', { count: reservation.disputes.length })}
                                                      </Typography>
                                                    )}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <Chip 
                                                    label={getReservationStatusLabel(reservation.reservation_status, t)}
                                                    size="small" 
                                                    variant="outlined"
                                                    color={getReservationStatusProps(reservation.reservation_status).color}
                                                  />
                                                  <Typography variant="h6" color="primary.main" fontWeight="bold">
                                                    {formatCurrency(reservation.payments?.reduce((sum, p) => {
                                                      // dispute_refund reduces what the owner keeps
                                                      if (p.payment_type === 'dispute_refund') return sum - parseFloat(p.amount);
                                                      return sum + parseFloat(p.amount);
                                                    }, 0) || 0, currency)}
                                                  </Typography>
                                                  <IconButton size="small" sx={{ ml: 1 }}>
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Box>
                                        </CardContent>

                                        {/* Détails expansés de la réservation */}
                                        <Collapse in={isExpanded}>
                                          <CardContent sx={{ pt: 0 }}>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            {/* 4 Payments principaux */}
                                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>
                                              {t('cash.details.paymentsHeader')}
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                              {reservation.payments?.map((payment, idx) => {
                                                const getPaymentTypeProps = (paymentType) => {
                                                  const typeProps = {
                                                    'rental': { icon: <HomeIcon />, color: 'success', label: t('cash.paymentTypes.rental') },
                                                    'deposit': { icon: <MoneyIcon />, color: 'warning', label: t('cash.paymentTypes.deposit') },
                                                    'commission': { icon: <CommissionIcon />, color: 'warning', label: t('cash.paymentTypes.commission') },
                                                    'tax': { icon: <TaxIcon />, color: 'info', label: t('cash.paymentTypes.tax') },
                                                    'dispute_refund': { icon: <TrendingDownIcon />, color: 'error', label: t('cash.paymentTypes.disputeRefund') },
                                                    'damage_compensation': { icon: <AddCompensationIcon />, color: 'success', label: t('cash.paymentTypes.damageCompensation') }
                                                  };
                                                  return typeProps[paymentType] || { icon: <MoneyIcon />, color: 'primary', label: paymentType };
                                                };
                                                
                                                const typeProps = getPaymentTypeProps(payment.payment_type);
                                                
                                                return (
                                                  <Grid item xs={12} sm={6} key={payment.payment_id}>
                                                    <Paper 
                                                      elevation={1}
                                                      sx={{ 
                                                        p: 2, 
                                                        border: 1, 
                                                        borderColor: `${typeProps.color}.main`,
                                                        backgroundColor: alpha(theme.palette[typeProps.color].main, 0.05)
                                                      }}
                                                    >
                                                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <Box color={theme.palette[typeProps.color].main}>
                                                          {typeProps.icon}
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" color={`${typeProps.color}.main`}>
                                                          {typeProps.label}
                                                        </Typography>
                                                      </Box>
                                                      <Typography variant="h6" fontWeight="bold" color={`${typeProps.color}.main`}>
                                                        {formatCurrency(payment.amount, currency)}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {t('cash.details.paymentStatus')} {payment.payment_status}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {t('cash.details.paymentDate')} {formatDateWithTime(payment.date, t)}
                                                      </Typography>
                                                    </Paper>
                                                  </Grid>
                                                );
                                              }) || []}
                                            </Grid>

                                            {/* Section Litiges */}
                                            {reservation.disputes && reservation.disputes.length > 0 && (() => {
                                              const hasResolvedDispute = reservation.disputes.some(d => ['resolved', 'closed'].includes(d.status) && d.compensation_amount);
                                              const rentalPayment = reservation.payments?.find(p => p.payment_type === 'rental');
                                              const rentalBase = rentalPayment ? parseFloat(rentalPayment.amount) : 0;
                                              const netRental = reservation.disputes
                                                .filter(d => ['resolved', 'closed'].includes(d.status) && d.compensation_amount)
                                                .reduce((acc, d) => {
                                                  if (d.compensation_type === 'owner_compensation') return acc + parseFloat(d.compensation_amount || 0);
                                                  if (d.compensation_type === 'renter_refund') return acc - parseFloat(d.compensation_amount || 0);
                                                  return acc;
                                                }, rentalBase);

                                              return (
                                                <>
                                                  <Divider sx={{ my: 2 }} />
                                                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                                    <GavelIcon color="warning" fontSize="small" />
                                                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                                      {t('cash.details.disputesHeader')}
                                                    </Typography>
                                                    <Chip label={reservation.disputes.length} size="small" color="warning" variant="outlined" />
                                                  </Box>

                                                  {reservation.disputes.map((dispute) => {
                                                    const isResolved = ['resolved', 'closed'].includes(dispute.status);
                                                    const disputeColor = !isResolved ? 'warning'
                                                      : dispute.compensation_type === 'renter_refund' ? 'error'
                                                      : dispute.compensation_type === 'owner_compensation' ? 'success'
                                                      : 'default';

                                                    return (
                                                      <Paper key={dispute.dispute_id} elevation={1} sx={{ p: 2, mb: 1.5, border: 1, borderColor: `${disputeColor}.main`, backgroundColor: alpha(theme.palette[disputeColor]?.main || theme.palette.grey[500], 0.05) }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                          <Box display="flex" alignItems="center" gap={1}>
                                                            {!isResolved ? <OverdueIcon color="warning" fontSize="small" /> : dispute.compensation_type === 'renter_refund' ? <TrendingDownIcon color="error" fontSize="small" /> : <AddCompensationIcon color="success" fontSize="small" />}
                                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                                              {dispute.title}
                                                            </Typography>
                                                          </Box>
                                                          <Chip label={t(`dispute.statusShow.${dispute.status}`, dispute.status)} size="small" color={disputeColor} variant="outlined" />
                                                        </Box>

                                                        {!isResolved && (
                                                          <Alert severity="warning" sx={{ mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                                                            {t('cash.details.disputeOngoingInfo')}
                                                          </Alert>
                                                        )}

                                                        {isResolved && (
                                                          <Box mt={1}>
                                                            <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                              {t('cash.details.disputeOutcome')}{': '}
                                                              <strong>{t(`dispute.compensationTypes.${dispute.compensation_type}`, dispute.compensation_type)}</strong>
                                                            </Typography>
                                                            {dispute.compensation_amount && (
                                                              <Typography variant="body1" fontWeight="bold" color={dispute.compensation_type === 'renter_refund' ? 'error.main' : dispute.compensation_type === 'owner_compensation' ? 'success.main' : 'text.primary'}>
                                                                {dispute.compensation_type === 'renter_refund' ? '- ' : dispute.compensation_type === 'owner_compensation' ? '+ ' : ''}
                                                                {formatCurrency(dispute.compensation_amount, dispute.compensation_currency || currency)}
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                  ({t('cash.details.disputeImpactOnRental')})
                                                                </Typography>
                                                              </Typography>
                                                            )}
                                                            {dispute.resolved_at && (
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                {t('cash.details.resolvedAt')}: {formatDateWithTime(dispute.resolved_at, t)}
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        )}
                                                      </Paper>
                                                    );
                                                  })}

                                                  {hasResolvedDispute && (
                                                    <Box sx={{ p: 2, mt: 1, backgroundColor: alpha(theme.palette.info.main, 0.08), borderRadius: 1, border: 1, borderColor: 'info.main' }}>
                                                      <Box display="flex" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                          {t('cash.details.netRentalAfterDispute')}
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight="bold" color="info.main">
                                                          {formatCurrency(netRental, currency)}
                                                        </Typography>
                                                      </Box>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {t('cash.details.commissionUnchanged')}
                                                      </Typography>
                                                    </Box>
                                                  )}
                                                </>
                                              );
                                            })()}

                                            {/* Actions */}
                                            {reservation.reservation_id && reservation.reservation_id !== 'unknown' && (
                                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  startIcon={<VisibilityIcon />}
                                                  onClick={() => handleShowReservationDetails(reservation.reservation_id)}
                                                >
                                                    {t('cash.details.seeReservation')}
                                                </Button>
                                              </Box>
                                            )}
                                          </CardContent>
                                        </Collapse>
                                      </Card>
                                    );
                                  });
                                  
                                  return reservationsToRender;
                                } else {
                                  // Pour pending_commission et completed_commission: traiter les réservations avec leurs commissions
                                  const reservationsToRender = detailsData.items.map((reservation, index) => {
                                    const reservationKey = `${expandedBalance}_${reservation.reservation_id}`;
                                    const isExpanded = expandedDetails[reservationKey];
                                    
                                    console.log(`🎯 [Commission] Rendering reservation:`, {
                                      reservation_id: reservation.reservation_id,
                                      payments_count: reservation.payments?.length || 0,
                                      payments: reservation.payments
                                    });
                                    
                                    // Trouver la ligne owner commission dans les payments
                                    let ownerCommission = null;
                                    const visiblePayments = []; // commission + tax uniquement
                                    
                                    if (reservation.payments) {
                                      reservation.payments.forEach(payment => {
                                        if (payment.payment_type === 'owner_commission') {
                                          ownerCommission = payment;
                                        } else if (['commission', 'tax'].includes(payment.payment_type)) {
                                          visiblePayments.push(payment);
                                        }
                                      });
                                    }
                                    
                                    const statusProps = ownerCommission
                                      ? getPayinStatusProps(ownerCommission.payment_status, t)
                                      : { color: 'default', icon: <ScheduleIcon />, label: t('unknown') };
                                    
                                    return (
                                      <Card 
                                        key={reservationKey} 
                                        sx={{ 
                                          mb: 2, 
                                          border: '2px solid', 
                                          borderColor: theme.palette.primary.main,
                                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                                        }}
                                      >
                                        {/* En-tête de la réservation */}
                                        <CardContent 
                                          sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                                          }}
                                          onClick={() => handleToggleExpansion(reservationKey)}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                                              <HomeIcon />
                                            </Avatar>
                                            
                                            <Box sx={{ flex: 1 }}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                  <Typography variant="subtitle1" fontWeight="bold">
                                                    {reservation.listing_title || t('cash.details.unknownListing')}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                    <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {t('cash.details.dateRange', {
                                                      from: new Date(reservation.start_date).toLocaleDateString(),
                                                      to: new Date(reservation.end_date).toLocaleDateString(),
                                                    })}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    <PersonIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                    {reservation.renter_name}
                                                  </Typography>
                                                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                                                    {t('cash.details.paymentsPlusOwnerCommissionCount', {
                                                      count: visiblePayments.length,
                                                    })}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <Chip 
                                                    label={statusProps.label}
                                                    size="small" 
                                                    variant="outlined"
                                                    color={statusProps.color}
                                                    icon={statusProps.icon}
                                                  />
                                                  {ownerCommission && (
                                                    <Typography variant="h6" color="primary.main" fontWeight="bold">
                                                      {formatCurrency(ownerCommission.amount, currency)}
                                                    </Typography>
                                                  )}
                                                  <IconButton size="small" sx={{ ml: 1 }}>
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Box>
                                        </CardContent>

                                        {/* Détails expansés de la réservation */}
                                        <Collapse in={isExpanded}>
                                          <CardContent sx={{ pt: 0 }}>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            {/* 4 Payments principaux */}
                                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>
                                              {t('cash.details.paymentsHeader')}
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                              {visiblePayments.map((payment, idx) => {
                                                const getPaymentTypeProps = (paymentType) => {
                                                  const typeProps = {
                                                    'commission':       { icon: <CommissionIcon />, color: 'warning', label: t('cash.paymentTypes.commission') },
                                                    'tax':              { icon: <TaxIcon />,        color: 'info',    label: t('cash.paymentTypes.tax') },
                                                    'owner_commission': { icon: <CommissionIcon />, color: 'primary', label: t('cash.paymentTypes.ownerCommission') }
                                                  };
                                                  return typeProps[paymentType] || { icon: <MoneyIcon />, color: 'primary', label: paymentType };
                                                };
                                                
                                                const typeProps = getPaymentTypeProps(payment.payment_type);
                                                
                                                return (
                                                  <Grid item xs={12} sm={6} key={payment.payment_id}>
                                                    <Paper 
                                                      elevation={1}
                                                      sx={{ 
                                                        p: 2, 
                                                        border: 1, 
                                                        borderColor: `${typeProps.color}.main`,
                                                        backgroundColor: alpha(theme.palette[typeProps.color].main, 0.05)
                                                      }}
                                                    >
                                                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                        <Box color={theme.palette[typeProps.color].main}>
                                                          {typeProps.icon}
                                                        </Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" color={`${typeProps.color}.main`}>
                                                          {typeProps.label}
                                                        </Typography>
                                                      </Box>
                                                      <Typography variant="h6" fontWeight="bold" color={`${typeProps.color}.main`}>
                                                        {formatCurrency(payment.amount, currency)}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {t('cash.details.paymentStatus')} {payment.payment_status}
                                                      </Typography>
                                                    </Paper>
                                                  </Grid>
                                                );
                                              })}
                                            </Grid>
                                            
                                            {/* Commission Owner */}
                                            {ownerCommission && (
                                              <>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
                                                  {t('cash.details.commissionHeader')}
                                                </Typography>
                                                <Paper 
                                                  elevation={2}
                                                  sx={{ 
                                                    p: 3, 
                                                    mb: 2,
                                                    border: 2,
                                                    borderColor: 'primary.main',
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                                  }}
                                                >
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}>
                                                      <Box display="flex" alignItems="center" gap={1}>
                                                        <CommissionIcon color="warning" />
                                                        <Box>
                                                          <Typography variant="caption" color="text.secondary">
                                                            {t('cash.details.commission')}
                                                          </Typography>
                                                          <Typography variant="body1" fontWeight="bold" color="warning.main">
                                                            {formatCurrency(ownerCommission.commission_amount || 0, currency)}
                                                          </Typography>
                                                        </Box>
                                                      </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                      <Box display="flex" alignItems="center" gap={1}>
                                                        <TaxIcon color="info" />
                                                        <Box>
                                                          <Typography variant="caption" color="text.secondary">
                                                            {t('cash.details.taxes')}
                                                          </Typography>
                                                          <Typography variant="body1" fontWeight="bold" color="info.main">
                                                            {formatCurrency(ownerCommission.taxes || 0, currency)}
                                                          </Typography>
                                                        </Box>
                                                      </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                      <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                          {t('cash.details.totalToReceive')}
                                                        </Typography>
                                                        <Typography variant="h5" fontWeight="bold" color="primary.main">
                                                          {formatCurrency(ownerCommission.amount, currency)}
                                                        </Typography>
                                                      </Box>
                                                    </Grid>
                                                  </Grid>
                                                  
                                                  {/* Dates importantes */}
                                                  <Divider sx={{ my: 2 }} />
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={6} sm={4}>
                                                      <Typography variant="caption" color="text.secondary">
                                                        {t('cash.details.createdDate')}
                                                      </Typography>
                                                      <Typography variant="body2">
                                                        {formatDateWithTime(ownerCommission.date, t)}
                                                      </Typography>
                                                    </Grid>
                                                    {ownerCommission.due_date && (() => {
                                                      // Calculer le 1er du mois suivant à partir de due_date
                                                      const dueDate = new Date(ownerCommission.due_date);
                                                      const nextMonthFirst = new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, 1);
                                                      return (
                                                        <Grid item xs={6} sm={4}>
                                                          <Typography variant="caption" color="text.secondary">
                                                            {t('cash.details.dueDate')}
                                                          </Typography>
                                                          <Typography variant="body2" color="warning.main">
                                                            {formatDateWithTime(nextMonthFirst, t)}
                                                          </Typography>
                                                        </Grid>
                                                      );
                                                    })()}
                                                    {ownerCommission.paid_date && (
                                                      <Grid item xs={6} sm={4}>
                                                        <Typography variant="caption" color="text.secondary">
                                                          {t('cash.details.paidDate')}
                                                        </Typography>
                                                        <Typography variant="body2" color="success.main">
                                                          {formatDateWithTime(ownerCommission.paid_date, t)}
                                                        </Typography>
                                                      </Grid>
                                                    )}
                                                  </Grid>
                                                </Paper>
                                              </>
                                            )}

                                            {/* Actions */}
                                            {reservation.reservation_id && reservation.reservation_id !== 'unknown' && (
                                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  startIcon={<VisibilityIcon />}
                                                  onClick={() => handleShowReservationDetails(reservation.reservation_id)}
                                                >
                                                    {t('cash.details.seeReservation')}
                                                </Button>
                                              </Box>
                                            )}
                                          </CardContent>
                                        </Collapse>
                                      </Card>
                                    );
                                  });
                                  
                                  return reservationsToRender;
                                }
                              })()}

                              {/* Bouton "Charger plus" - Pagination infinie */}
                              {(() => {
                                const hasMoreData = detailsData.pagination && detailsData.pagination.hasMore;
                                console.log(`🔍 [LoadMore Debug] Condition check for ${expandedBalance}:`, {
                                  detailsData: detailsData,
                                  pagination: detailsData.pagination,
                                  hasMore: detailsData.pagination?.hasMore,
                                  hasMoreData,
                                  willShowButton: hasMoreData
                                });
                                
                                return hasMoreData ? (
                                  <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    mt={3}
                                    sx={{
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      pt: 2
                                    }}
                                  >
                                    <Button 
                                      variant="outlined" 
                                      color="primary"
                                      size="large"
                                      startIcon={isLoadingDetails ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                                      onClick={() => {
                                        const [currency, balanceType] = expandedBalance.split('_');
                                        loadMoreDetails(currency, balanceType);
                                      }}
                                      disabled={isLoadingDetails}
                                      sx={{
                                        minWidth: 200,
                                        py: 1.5,
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                        }
                                      }}
                                    >
                                      {isLoadingDetails ? (
                                        t('cash.details.loadingMore')
                                      ) : (
                                        <>
                                          {balanceType === 'current' || balanceType === 'pending'
                                            ? t('cash.details.loadMorePayments')
                                            : t('cash.details.loadMoreReservations')}
                                          {detailsData.pagination.total && detailsData.items.length
                                            ? t('cash.details.loadMoreCount', {
                                                count: detailsData.pagination.total - detailsData.items.length,
                                              })
                                            : ''}
                                        </>
                                      )}
                                    </Button>
                                  </Box>
                                ) : (
                                  <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    mt={3}
                                    sx={{
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                      pt: 2
                                    }}
                                  >
                                    <Typography variant="body2" color="text.secondary" align="center">
                                      {t('cash.details.allLoaded')}
                                      {detailsData.pagination?.total
                                        ? t('cash.details.allLoadedCount', { count: detailsData.pagination.total })
                                        : ''}
                                    </Typography>
                                  </Box>
                                );
                              })()}

                              {/* Informations de pagination */}
                              {detailsData.pagination && (
                                <Box 
                                  display="flex" 
                                  justifyContent="center" 
                                  mt={2}
                                >
                                  <Typography variant="caption" color="text.secondary" align="center">
                                    {(() => {
                                      const [currency, balanceType] = expandedBalance.split('_');
                                      if (balanceType === 'current' || balanceType === 'pending') {
                                        return t('cash.details.paginationPayments', { shown: detailsData.items.length });
                                      } else {
                                        const reservationCount = [...new Set(detailsData.items.map(item => item.reservation_id))].length;
                                        return t('cash.details.paginationReservations', {
                                          reservations: reservationCount,
                                          lines: detailsData.items.length,
                                        });
                                      }
                                    })()}
                                    {detailsData.pagination.total && (
                                      t('cash.details.paginationTotalSuffix', { total: detailsData.pagination.total })
                                    )}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Box textAlign="center" py={4}>
                              <Typography variant="body1" color="text.secondary">
                                {(() => {
                                  const [currency, balanceType] = expandedBalance.split('_');
                                  if (balanceType === 'current' || balanceType === 'pending') {
                                    return t('cash.details.noPayments');
                                  } else {
                                    return t('cash.details.noCommissions');
                                  }
                                })()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </React.Fragment>
        ))}

        {/* Section conversion totale - ADAPTÉE pour CASH */}
        <Divider sx={{ my: 3 }} />
        
        <Card 
          variant="outlined"
          sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: 2,
            borderColor: 'primary.main'
          }}
        >
          <CardContent>
            {/* En-tête conversion */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <ConvertIcon color="primary" />
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {t('cash.conversion.title')}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('cash.conversion.target')}</InputLabel>
                <Select
                  value={targetCurrency}
                  onChange={handleTargetCurrencyChange}
                  label={t('cash.conversion.target')}
                >
                  {cashData.available_currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Métriques converties - 4 CATÉGORIES pour CASH */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={3}>
                <BalanceMetric
                  title={t('cash.conversion.currentTotal')}
                  value={cashData.converted_totals.current_balance}
                  currency={cashData.converted_totals.currency}
                  icon={<CheckCircleIcon />}
                  color="success"
                  infoType="currentBalance"
                  balanceType="current"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <BalanceMetric
                  title={t('cash.conversion.pendingTotal')}
                  value={cashData.converted_totals.pending_balance}
                  currency={cashData.converted_totals.currency}
                  icon={<PendingIcon />}
                  color="info"
                  infoType="pendingBalance"
                  balanceType="pending"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <BalanceMetric
                  title={t('cash.conversion.pendingCommissionTotal')}
                  value={cashData.converted_totals.pending_commission_balance}
                  currency={cashData.converted_totals.currency}
                  icon={<ScheduleIcon />}
                  color="warning"
                  infoType="pendingCommissionBalance"
                  balanceType="pending_commission"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <BalanceMetric
                  title={t('cash.conversion.completedCommissionTotal')}
                  value={cashData.converted_totals.completed_commission_balance}
                  currency={cashData.converted_totals.currency}
                  icon={<CompletedIcon />}
                  color="success"
                  infoType="completedCommissionBalance"
                  balanceType="completed_commission"
                />
              </Grid>
            </Grid>

            {/* Breakdown commission/taxes */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                    {t('cash.conversion.totalCommissions')}
                  </Typography>
                  <Typography variant="h6" color="warning.main" fontWeight="bold">
                    {formatCurrency(cashData.converted_totals.total_commission, cashData.converted_totals.currency)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="caption" color="info.main" fontWeight="bold">
                    {t('cash.conversion.totalTaxes')}
                  </Typography>
                  <Typography variant="h6" color="info.main" fontWeight="bold">
                    {formatCurrency(cashData.converted_totals.total_taxes, cashData.converted_totals.currency)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Informations sur les taux de change */}
            <Box 
              sx={{ 
                mt: 2, 
                p: 1.5, 
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 1,
                border: 1,
                borderColor: 'info.main'
              }}
            >
              <Typography variant="caption" color="info.main" fontWeight="bold">
                {t('cash.conversion.note')}
              </Typography>
              <Typography variant="caption" color="text.secondary" ml={1}>
                {t('cash.conversion.rateInfo')}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Modal d'information */}
        <Dialog open={isInfoModalOpen} onClose={closeInfoModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {infoModalContent.title}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {infoModalContent.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeInfoModal} color="primary">
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de détails de réservation */}
        {selectedReservationId && (
          <ReservationDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            reservationId={selectedReservationId}
          />
        )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default OwnerCashBalancesWidget;